import React from 'react';

import classNames from 'classnames';
import { useQuery } from 'react-query';
import { Icon } from 'src/common/components/Icon';
import { OrbitIcons } from 'src/common/constants.';

import { fetchCallModule, fetchOfficeSchedule, fetchCallGroup, fetchPlaybackAudio } from '../api';
import type { CallModuleDetail, CallModuleConfig } from '../api';

interface CallModuleDetailViewProps {
  moduleId: number;
  onClose: () => void;
  onEdit: () => void;
  onDelete: (moduleId: number) => void;
}

const CONFIG_TYPE_LABELS: Record<string, string> = {
  agent_route: 'Ruta a agentes',
  message_route: 'Mensaje grabado',
  external_number_route: 'Número externo',
};

const SCHEDULE_TYPE_LABELS: Record<string, string> = {
  in_office: 'Horario de oficina',
  out_of_office: 'Fuera de horario',
  time_off: 'Día libre / Feriado',
};

const InfoRow: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
  <div className="flex justify-between border-b border-white-2.0 py-2 last:border-b-0">
    <span className="text-xs text-gray-2.0">{label}</span>
    <span className="text-sm font-medium text-black-1.0">{value ?? '—'}</span>
  </div>
);

const ConfigCard: React.FC<{
  config: CallModuleConfig;
  accountId: number;
}> = ({ config, accountId }) => {
  const isAgentRoute = config.config_type === 'agent_route';
  const isMessageRoute = config.config_type === 'message_route';

  const { data: callGroup } = useQuery(
    ['call-group', isAgentRoute ? (config as { group_id: number }).group_id : null],
    () =>
      isAgentRoute
        ? fetchCallGroup((config as { group_id: number }).group_id)
        : Promise.resolve(null),
    { enabled: isAgentRoute, refetchOnWindowFocus: false }
  );

  const { data: greetingAudio } = useQuery(
    [
      'playback-audio',
      isAgentRoute ? (config as { greeting_playback_id: number }).greeting_playback_id : null,
    ],
    () =>
      isAgentRoute
        ? fetchPlaybackAudio((config as { greeting_playback_id: number }).greeting_playback_id)
        : Promise.resolve(null),
    { enabled: isAgentRoute, refetchOnWindowFocus: false }
  );

  const { data: messageAudio } = useQuery(
    [
      'playback-audio',
      isMessageRoute ? (config as { message_playback_id: number }).message_playback_id : null,
    ],
    () =>
      isMessageRoute
        ? fetchPlaybackAudio((config as { message_playback_id: number }).message_playback_id)
        : Promise.resolve(null),
    { enabled: isMessageRoute, refetchOnWindowFocus: false }
  );

  return (
    <div className="rounded border border-white-2.0 bg-white-1.0 p-3">
      <div className="flex items-center gap-2 mb-2">
        <span
          className={classNames(
            'inline-flex items-center rounded px-2 py-0.5 text-xs font-medium',
            config.schedule_type === 'in_office'
              ? 'bg-green-100 text-green-700'
              : config.schedule_type === 'out_of_office'
              ? 'bg-orange-100 text-orange-700'
              : 'bg-blue-100 text-blue-700'
          )}
        >
          {SCHEDULE_TYPE_LABELS[config.schedule_type]}
        </span>
        <span className="text-xs text-gray-2.0">{CONFIG_TYPE_LABELS[config.config_type]}</span>
      </div>

      {isAgentRoute && (
        <div className="space-y-1 text-xs">
          <InfoRow label="Grupo" value={callGroup?.name ?? '...'} />
          <InfoRow label="Audio de saludo" value={greetingAudio?.name ?? '...'} />
          <InfoRow
            label="Tiempo máx. de espera"
            value={`${(config as { max_wait_time: number }).max_wait_time}s`}
          />
          <InfoRow
            label="Llamadas máx. en cola"
            value={(config as { max_calls_on_wait: number }).max_calls_on_wait}
          />
        </div>
      )}

      {isMessageRoute && (
        <div className="space-y-1 text-xs">
          <InfoRow label="Audio de mensaje" value={messageAudio?.name ?? '...'} />
        </div>
      )}

      {config.config_type === 'external_number_route' && (
        <div className="space-y-1 text-xs">
          <InfoRow
            label="Número externo"
            value={(config as { external_phone_number: number }).external_phone_number}
          />
        </div>
      )}
    </div>
  );
};

export const CallModuleDetailView: React.FC<CallModuleDetailViewProps> = ({
  moduleId,
  onClose,
  onEdit,
  onDelete,
}) => {
  const {
    data: module,
    isLoading,
    error,
  } = useQuery(['call-module', moduleId], () => fetchCallModule(moduleId), {
    refetchOnWindowFocus: false,
  });

  const { data: schedule } = useQuery(
    ['office-schedule', module?.schedule_id],
    () => (module?.schedule_id ? fetchOfficeSchedule(module.schedule_id) : Promise.resolve(null)),
    { enabled: Boolean(module?.schedule_id), refetchOnWindowFocus: false }
  );

  if (isLoading) {
    return (
      <div className="p-4">
        <p className="text-sm text-gray-2.0">Cargando detalles del módulo...</p>
      </div>
    );
  }

  if (error || !module) {
    return (
      <div className="p-4">
        <p className="text-sm text-red-500">Error al cargar el módulo</p>
        <button type="button" onClick={onClose} className="btn btn-ghost btn-sm mt-2">
          Volver
        </button>
      </div>
    );
  }

  const inOfficeConfig = module.configs.find((c) => c.schedule_type === 'in_office');
  const outOfOfficeConfig = module.configs.find((c) => c.schedule_type === 'out_of_office');
  const timeOffConfigs = module.configs.filter((c) => c.schedule_type === 'time_off');

  return (
    <div className="p-4">
      <div className="flex items-start justify-between mb-4">
        <div>
          <h3 className="text-lg font-bold text-black-1.0">{module.name}</h3>
          <p className="font-mono text-sm text-gray-2.0">{module.phone_number}</p>
        </div>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={onEdit}
            className="btn btn-ghost btn-sm flex items-center gap-1"
            title="Editar módulo (próximamente)"
            disabled
          >
            <Icon name={OrbitIcons.Pencil} size={14} />
            Editar
          </button>
          <button
            type="button"
            onClick={() => {
              if (window.confirm('¿Eliminar este módulo de llamada?')) {
                onDelete(moduleId);
              }
            }}
            className="btn btn-ghost btn-sm hover:text-red-700 flex items-center gap-1 text-red-500"
          >
            <Icon name={OrbitIcons.Trash} size={14} />
            Eliminar
          </button>
          <button type="button" onClick={onClose} className="btn btn-ghost btn-sm">
            <Icon name={OrbitIcons.X} size={14} />
          </button>
        </div>
      </div>

      <div className="mb-4 rounded border border-white-2.0 bg-white-0.0 px-4 py-3">
        <InfoRow label="Horario asignado" value={schedule?.name ?? module.schedule?.name ?? '—'} />
      </div>

      <div className="space-y-4">
        <div>
          <h4 className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-2.0">
            Configuración en horario
          </h4>
          {inOfficeConfig ? (
            <ConfigCard config={inOfficeConfig} accountId={module.account_id ?? 0} />
          ) : (
            <p className="text-sm text-gray-2.0">No configurado</p>
          )}
        </div>

        <div>
          <h4 className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-2.0">
            Configuración fuera de horario
          </h4>
          {outOfOfficeConfig ? (
            <ConfigCard config={outOfOfficeConfig} accountId={module.account_id ?? 0} />
          ) : (
            <p className="text-sm text-gray-2.0">No configurado</p>
          )}
        </div>

        {timeOffConfigs.length > 0 && (
          <div>
            <h4 className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-2.0">
              Configuraciones especiales ({timeOffConfigs.length})
            </h4>
            <div className="space-y-2">
              {timeOffConfigs.map((config, index) => (
                <ConfigCard key={index} config={config} accountId={module.account_id ?? 0} />
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
